// Espiar um negócio sem sair do quadro — o painel lateral de "preview" dos CRMs
// de referência (HubSpot). Abrir o negócio inteiro troca a tela e, na volta, o
// quadro recarrega e a pessoa perde o lugar onde estava; para a pergunta "o que
// é este cartão e o que falta nele?", a espiada basta: etapa (e mover de etapa),
// próximo passo, quem é o cliente, as últimas interações e um campo para anotar.
//
// Tudo aqui usa rota que já existia. O negócio completo continua a um clique.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Drawer, Button, Typography, Tag, Input, Space, Spin, App as AntApp, Descriptions } from 'antd';
import { ArrowRightOutlined, SendOutlined, BankOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDataBr } from '../../utils/dataFmt';
import { Proposta } from './types';
import { fmt, STATUS_LABELS, STATUS_COLORS } from './utils';
import {
  desdeQuandoNaEtapa, diasDesdeData, ehAberto, valorDoNegocio, valorPonderado,
} from './saudeNegocio';
import { AvatarDono, SeloParado } from './pecas';
import { TrilhaEtapas } from '../negocio-detalhe/TrilhaEtapas';
import { ProximoPassoBloco } from '../negocio-detalhe/ProximoPassoBloco';
import { EventoItem } from '../negocio-detalhe/EventoItem';

const { Text } = Typography;

export function DrawerNegocio({ negocio, onFechar, onAbrirCompleto, onMudarStatus, onMudou }: {
  negocio: Proposta;
  onFechar: () => void;
  onAbrirCompleto: () => void;
  /** Sem ele (sem permissão de mudar etapa), a trilha fica só de leitura. */
  onMudarStatus?: (id: number, novo: string) => void;
  /** Algo mudou (atividade criada/concluída): o quadro relê os próximos passos. */
  onMudou: () => void;
}) {
  const { message } = AntApp.useApp();
  const [eventos, setEventos] = useState<any[] | null>(null);
  const [nota, setNota] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function carregarEventos() {
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/comercial/propostas/${negocio.id}/eventos`,
        { headers: { Authorization: token } });
      const data = r.data?.body ? JSON.parse(r.data.body) : r.data;
      setEventos(Array.isArray(data) ? data : []);
    } catch { setEventos([]); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setEventos(null); carregarEventos(); }, [negocio.id, negocio.status]);

  async function anotar() {
    if (!nota.trim()) return;
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas/${negocio.id}/eventos`,
        { tipo: 'nota', descricao: nota.trim() }, { headers: { Authorization: token } });
      setNota('');
      await carregarEventos();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvando(false); }
  }

  const st = STATUS_COLORS[negocio.status] || STATUS_COLORS.prospeccao;
  const aberto = ehAberto(negocio.status);
  const desde = desdeQuandoNaEtapa(negocio.status, eventos || [], negocio.criado_em || negocio.data_abertura);
  const diasNaEtapa = eventos === null ? null : diasDesdeData(desde);
  const recentes = (eventos || []).slice()
    .sort((a, b) => new Date(b.criado_em).getTime() - new Date(a.criado_em).getTime()).slice(0, 6);

  return (
    <Drawer open onClose={onFechar} width="min(96vw, 520px)" destroyOnHidden
      title={
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#0E1433', lineHeight: 1.3 }}>{negocio.titulo}</div>
          <div style={{ fontSize: 12, fontWeight: 400, color: '#5A6378', marginTop: 2 }}>
            <BankOutlined /> {negocio.cliente_nome || 'sem empresa'}
            {negocio.origem === 'licitacao' && <Tag color="purple" style={{ marginLeft: 8, fontSize: 10 }}>Licitação</Tag>}
          </div>
        </div>
      }
      footer={
        <Button type="primary" block icon={<ArrowRightOutlined />} iconPlacement="end" onClick={onAbrirCompleto}>
          Abrir negócio completo
        </Button>
      }>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#020D7F', lineHeight: 1.1 }}>{fmt(valorDoNegocio(negocio))}</div>
          {aberto && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {negocio.probabilidade ?? 0}% · ponderado {fmt(valorPonderado(negocio))}
            </Text>
          )}
        </div>
        <Tag style={{ margin: 0, background: st.bg, color: st.color, border: 'none', fontWeight: 600 }}>
          {STATUS_LABELS[negocio.status] || negocio.status}
        </Tag>
        {aberto && diasNaEtapa != null && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            na etapa {diasNaEtapa === 0 ? 'desde hoje' : diasNaEtapa === 1 ? 'há 1 dia' : `há ${diasNaEtapa} dias`}
          </Text>
        )}
        <span style={{ marginLeft: 'auto' }}><SeloParado negocio={negocio} /></span>
      </div>

      <div style={{ marginBottom: 12 }}>
        <TrilhaEtapas compacta status={negocio.status} diasNaEtapa={diasNaEtapa}
          onMudar={onMudarStatus && ((s) => onMudarStatus(negocio.id, s))}
          onGanhar={onMudarStatus && (() => onMudarStatus(negocio.id, 'ganho'))} />
        {negocio.status === 'perdido' && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
            Motivo da perda: <b style={{ color: '#8B3A3A' }}>{negocio.motivo_perda || 'não informado'}</b>
          </Text>
        )}
      </div>

      <ProximoPassoBloco compacto negocio={negocio} onMudou={onMudou} />

      <Descriptions size="small" column={1} colon={false} style={{ marginBottom: 12 }}
        styles={{ label: { color: '#5A6378', fontSize: 12, width: 104 }, content: { fontSize: 12.5 } }}
        items={[
          { key: 'r', label: 'Responsável', children: (
            <Space size={6}><AvatarDono nome={negocio.responsavel} tamanho={18} />
              {negocio.responsavel || <Text type="secondary">sem responsável</Text>}</Space>
          ) },
          ...(negocio.contato_nome ? [{ key: 'c', label: 'Contato', children: (
            <span>{negocio.contato_nome}{negocio.contato_email && (
              <> · <a href={`mailto:${negocio.contato_email}`}>{negocio.contato_email}</a></>
            )}</span>
          ) }] : []),
          ...(negocio.area ? [{ key: 'a', label: 'Área', children: negocio.area }] : []),
          { key: 'd', label: 'Abertura', children: fmtDataBr(negocio.data_abertura) },
          ...(negocio.data_envio ? [{ key: 'e', label: 'Proposta enviada', children: fmtDataBr(negocio.data_envio) }] : []),
          ...(negocio.observacoes && !/^Importado da planilha Ploomes/i.test(negocio.observacoes.trim())
            ? [{ key: 'o', label: 'Observações', children: (
              <span style={{ whiteSpace: 'pre-wrap' }}>
                {negocio.observacoes.length > 400 ? negocio.observacoes.slice(0, 400) + '…' : negocio.observacoes}
              </span>
            ) }] : []),
        ]} />

      <Text style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: '#5A6378' }}>
        Últimas interações
      </Text>
      <Space.Compact style={{ width: '100%', margin: '6px 0 4px' }}>
        <Input.TextArea value={nota} onChange={e => setNota(e.target.value)} disabled={salvando}
          placeholder="Anotar uma ligação, e-mail ou observação…" autoSize={{ minRows: 1, maxRows: 4 }} />
        <Button type="primary" icon={<SendOutlined />} onClick={anotar} loading={salvando}
          disabled={!nota.trim()} aria-label="Registrar" />
      </Space.Compact>
      {eventos === null ? (
        <div style={{ padding: 20, textAlign: 'center' }}><Spin size="small" /></div>
      ) : recentes.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>Nenhuma interação registrada neste negócio.</Text>
      ) : (
        <div>
          {recentes.map(ev => <EventoItem key={ev.id} evento={ev} onExcluir={null} />)}
          {(eventos || []).length > recentes.length && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              +{(eventos || []).length - recentes.length} mais antigas no negócio completo
            </Text>
          )}
        </div>
      )}
    </Drawer>
  );
}
